export interface GuideCareer {
  title: string
  desc: string
  salary: string
}

export interface GuideFAQ {
  q: string
  a: string
}

export interface GuideBenefit {
  icon:
    | 'ClockIcon'
    | 'TrendingUpIcon'
    | 'GlobeIcon'
    | 'BookOpenIcon'
    | 'ShieldCheckIcon'
    | 'HeartIcon'
    | 'UsersIcon'
    | 'CheckCircleIcon'
  title: string
  description: string
}

export interface GuideRelatedLink {
  label: string
  href: string
}

export interface Guide {
  slug: string
  title: string // e.g. "Online Christian Colleges"
  metaTitle: string
  metaDescription: string
  breadcrumbLabel: string
  // Hero
  heroEyebrow?: string
  heroHeadline: string
  heroDescription: string
  // API filters used to populate matched schools
  focusValue: string // maps to focusOptions value in data/degrees.ts
  defaultSpecialization?: string // optional default specialization value
  // Sidebar "what to look for" list
  sidebarChecklistTitle: string
  sidebarChecklist: string[]
  // Content sections
  overviewHeading: string
  overviewParagraphs: string[]
  benefitsHeading: string
  benefitsIntro: string
  benefits: GuideBenefit[]
  careersHeading: string
  careersIntro: string
  careers: GuideCareer[]
  faqHeading: string
  faqIntro: string
  faqs: GuideFAQ[]
  // CTAs / related
  relatedLinks: GuideRelatedLink[]
}

export const guides: Guide[] = [
  {
    slug: 'online-christian-colleges',
    title: 'Online Christian Colleges',
    metaTitle:
      'Best Online Christian Colleges & Universities | FindOnlineColleges',
    metaDescription:
      'Compare accredited online Christian colleges and faith-based universities. Explore Bible, ministry, theology, and traditional degrees grounded in Christian values.',
    breadcrumbLabel: 'Online Christian Colleges',
    heroEyebrow: 'A Guide To',
    heroHeadline: 'Online Christian Colleges & Universities',
    heroDescription:
      'Earn a respected, accredited degree from a faith-based university — on your schedule. Compare top online Christian colleges offering Bible, ministry, business, nursing, psychology, and more.',
    focusValue: '13', // Religious Studies
    defaultSpecialization: '1300', // All Religious Studies Programs (General)
    sidebarChecklistTitle: 'What to Look For',
    sidebarChecklist: [
      'Regional or national accreditation',
      'Statement of faith that aligns with you',
      'Flexible online course formats',
      'Transfer credit & military-friendly policies',
      'Affordable tuition with faith-based aid',
      'Chapel, mentorship & community life online',
    ],
    overviewHeading: 'What is an Online Christian College?',
    overviewParagraphs: [
      'An online Christian college is an accredited university that integrates a biblical worldview into every program — from theology and ministry to business, nursing, education, and counseling. Students earn the same kinds of degrees offered at secular institutions, but coursework is taught through the lens of Christian values, ethics, and Scripture.',
      'Today, leading faith-based universities like Liberty, Grand Canyon, Colorado Christian, Regent, and Indiana Wesleyan offer fully online programs designed for working adults. You can study at your own pace, connect with faculty who share your faith, and apply what you learn to your church, community, or career — without setting foot on campus.',
      "Whether you're called to ministry or simply want your education rooted in your beliefs, an online Christian college can help you grow intellectually, professionally, and spiritually at the same time.",
    ],
    benefitsHeading: 'Why Choose an Online Christian College?',
    benefitsIntro:
      'Faith-based online programs offer the same flexibility as secular online schools — with the added benefit of community, mentorship, and a values-driven education.',
    benefits: [
      {
        icon: 'BookOpenIcon',
        title: 'Faith-Integrated Curriculum',
        description:
          'Every course — from accounting to psychology — is taught from a biblical worldview, helping you connect what you learn with what you believe.',
      },
      {
        icon: 'UsersIcon',
        title: 'Christian Community Online',
        description:
          'Virtual chapel services, prayer groups, mentorship, and discussion forums help you build real relationships with students and faculty who share your values.',
      },
      {
        icon: 'ClockIcon',
        title: 'Flexible for Working Adults',
        description:
          'Accelerated 8-week courses, asynchronous learning, and rolling start dates make it easy to balance school with ministry, family, and work.',
      },
      {
        icon: 'HeartIcon',
        title: 'Service-Minded Mission',
        description:
          'Christian universities prepare graduates not just for careers, but for lives of service — at home, in the workplace, and around the world.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Accredited & Respected',
        description:
          'Top Christian colleges hold regional accreditation, meaning your degree is recognized by employers, graduate schools, and licensing boards.',
      },
      {
        icon: 'TrendingUpIcon',
        title: 'Affordable Tuition Options',
        description:
          'Many faith-based universities offer reduced online tuition, generous transfer credit, and scholarships for ministry, military, and first-time students.',
      },
    ],
    careersHeading: 'Careers You Can Pursue with a Christian College Degree',
    careersIntro:
      'A degree from a Christian college opens doors to ministry roles as well as a wide range of professional careers in business, healthcare, education, and counseling.',
    careers: [
      {
        title: 'Pastor or Ministry Leader',
        desc: 'Lead a congregation, plant a church, or serve in a specialized ministry role focused on teaching, worship, or outreach.',
        salary: '$45,000 - $75,000',
      },
      {
        title: 'Christian Counselor',
        desc: 'Help individuals, couples, and families navigate life challenges through faith-informed, evidence-based counseling.',
        salary: '$50,000 - $80,000',
      },
      {
        title: 'Missionary or Nonprofit Leader',
        desc: 'Serve communities at home or abroad through Christian nonprofits, relief organizations, and faith-based missions.',
        salary: '$40,000 - $70,000',
      },
      {
        title: 'Christian School Teacher',
        desc: 'Teach K-12 students in private Christian schools, integrating academics with biblical principles and character formation.',
        salary: '$42,000 - $65,000',
      },
      {
        title: 'Youth or Worship Pastor',
        desc: 'Disciple students or lead worship within a local church, often combining teaching, mentoring, and event leadership.',
        salary: '$38,000 - $60,000',
      },
      {
        title: 'Chaplain',
        desc: 'Provide spiritual care in hospitals, the military, prisons, or corporate settings as a credentialed chaplain.',
        salary: '$50,000 - $80,000',
      },
      {
        title: 'Faith-Based Business Leader',
        desc: 'Apply Christian ethics and stewardship to roles in management, finance, marketing, or entrepreneurship.',
        salary: '$60,000 - $110,000',
      },
      {
        title: 'Nonprofit or Church Administrator',
        desc: 'Manage operations, finances, and staff for churches, ministries, and Christian nonprofits.',
        salary: '$50,000 - $85,000',
      },
    ],
    faqHeading: 'Frequently Asked Questions',
    faqIntro:
      'Common questions about earning your degree from an online Christian college.',
    faqs: [
      {
        q: 'Are online Christian colleges accredited?',
        a: 'Yes. Most reputable online Christian colleges — such as Liberty University, Grand Canyon University, Regent University, and Colorado Christian University — hold regional accreditation from the same agencies that accredit secular universities. Some specialty Bible colleges also hold accreditation from the Association for Biblical Higher Education (ABHE) or the Transnational Association of Christian Colleges and Schools (TRACS).',
      },
      {
        q: 'Do I have to be Christian to attend an online Christian college?',
        a: 'No. While Christian colleges are rooted in faith, most welcome students from all backgrounds. Students are generally expected to respect the school’s statement of faith and community values, but coursework, chapel, and spiritual activities are designed to be a positive experience whether or not you identify as Christian.',
      },
      {
        q: 'What kinds of degrees can I earn online from a Christian university?',
        a: 'Beyond ministry, theology, and biblical studies, you can earn online degrees in business, nursing, education, psychology, counseling, criminal justice, IT, social work, and more. Every program integrates a Christian worldview, but the academic content is comparable to programs at secular institutions.',
      },
      {
        q: 'How much does an online Christian college cost?',
        a: 'Tuition varies widely. Many online Christian universities offer reduced online tuition rates — often between $300 and $500 per credit hour — along with scholarships for ministry, military, first responders, and transfer students. Federal financial aid is available at most regionally accredited Christian colleges.',
      },
      {
        q: 'Can I transfer my previous college credits?',
        a: 'Almost always, yes. Most online Christian colleges accept generous transfer credit — sometimes up to 75% of a bachelor’s degree — from prior coursework, military training, and approved certifications. This can significantly reduce the time and cost to graduate.',
      },
      {
        q: 'Will employers respect a degree from an online Christian college?',
        a: 'Yes. Employers care most about accreditation and the skills you bring to the role. A degree from a regionally accredited Christian university carries the same weight as one from a secular school, and many graduates find their faith-informed perspective is an asset in leadership, healthcare, education, and counseling roles.',
      },
    ],
    relatedLinks: [
      { label: 'Religious Studies Programs', href: '/degrees/liberal-arts' },
      { label: 'Counseling & Psychology', href: '/degrees/psychology' },
      { label: 'Education & Teaching', href: '/degrees/education' },
      {
        label: 'Colorado Christian University',
        href: '/colleges/colorado-christian-university',
      },
    ],
  },
]

export function getGuideBySlug(slug: string): Guide | undefined {
  return guides.find((g) => g.slug === slug)
}
